import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BookOpen, Sparkles } from "lucide-react";
import { API_BASE, MAX_CHANNEL_LINK } from "@/config/links";
import { LANDING } from "@/constants/testIds";
import { trackClick } from "@/lib/track";
import { fadeUp, stagger, word, wordsContainer } from "@/lib/motion";
import StarField from "@/components/StarField";
import HERO_IMAGE_URL from "@/assets/hero-avatar.webp";

const TITLE = "Сказка, в которой главный герой — ваш ребёнок";

const Hero = () => {
  const sectionRef = useRef(null);
  const reduced = useReducedMotion();
  const [stats, setStats] = useState(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const moonY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 120]);
  const cloudY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -60]);

  // Счётчик — социальное доказательство. Пока сказок мало, цифра работает против нас, поэтому прячем.
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/stats`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("no stats"))))
      .then((data) => {
        if (!alive || !data?.ok) return;
        const stories = Number(data.stories) || 0;
        const children = Number(data.children) || 0;
        if (stories >= 20) setStats({ stories, children });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-night-900 pb-16 pt-12 md:pb-28 md:pt-20"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-night-950 via-night-900 to-night-800" />
      <StarField />

      {/* Луна и облачная дымка — лёгкий параллакс по скроллу */}
      <motion.div
        style={{ y: moonY }}
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 top-8 h-40 w-40 rounded-full bg-amber-soft/90 blur-[2px] sm:right-10 md:h-52 md:w-52"
      >
        <div className="absolute inset-0 rounded-full bg-amber/30 blur-3xl" />
      </motion.div>
      <motion.div
        style={{ y: cloudY }}
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-[radial-gradient(60%_70%_at_20%_100%,rgba(120,110,220,0.35),transparent_70%),radial-gradient(50%_60%_at_80%_100%,rgba(245,183,74,0.18),transparent_70%)]"
      />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 md:grid-cols-[1.05fr_0.95fr]">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger(0.12)}
          className="text-center md:text-left"
        >
          {stats ? (
            <motion.div variants={fadeUp} className="mb-5 flex justify-center md:justify-start">
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white/[0.05] px-3.5 py-1.5 text-xs font-medium text-amber-soft backdrop-blur">
                <BookOpen className="h-3.5 w-3.5" />
                Уже {stats.stories} сказок для {stats.children} детей
              </span>
            </motion.div>
          ) : null}

          <motion.h1
            variants={wordsContainer}
            className="mb-6 font-serif text-[2.6rem] leading-[1.05] text-cream sm:text-6xl lg:text-7xl"
          >
            {TITLE.split(" ").map((w, i) => (
              <motion.span key={`${w}-${i}`} variants={word} className="mr-[0.25em] inline-block">
                {w}
              </motion.span>
            ))}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mx-auto mb-8 max-w-xl text-base leading-relaxed text-muted-foreground md:mx-0 md:text-lg"
          >
            Назовите имя, возраст и что сейчас волнует: садик, темнота, зубы. Через пару
            минут готова история с озвучкой и картинкой. Первая — бесплатно.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col items-center gap-4 md:items-start">
            <Link
              data-testid={LANDING.heroPrimaryCta}
              to="/chat"
              onClick={() => trackClick("chat")}
              className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-amber px-7 text-base font-semibold text-night-900 shadow-glow transition-all duration-300 hover:bg-amber-hover hover:shadow-glow-lg active:scale-[0.98] sm:w-auto"
            >
              <Sparkles className="h-5 w-5 shrink-0" />
              Сделать сказку бесплатно
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
            <a
              data-testid={LANDING.heroSecondaryCta}
              href={MAX_CHANNEL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick("channel")}
              className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-amber-soft"
            >
              Бесплатные сказки каждый вечер в MAX
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="flex justify-center md:justify-end"
        >
          <div className="relative w-60 max-w-sm sm:w-80 md:w-full">
            <div className="absolute -inset-6 rounded-[3rem] bg-amber/20 blur-3xl" />
            <div className="relative animate-float">
              <img
                data-testid={LANDING.heroImage}
                src={HERO_IMAGE_URL}
                alt="Мама читает ребёнку сказку на ночь"
                className="w-full rounded-[2.5rem] border border-line object-cover shadow-lift"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
